import React, { Suspense, lazy, useCallback, useEffect, useState } from 'react';
import {
  useProductBranding,
  useUpdateProductBranding,
} from '../../../../../shared/hooks/useProductBranding';

const Markdown = lazy(async () => {
  const [{ default: ReactMarkdown }, { default: remarkGfm }] = await Promise.all([
    import('react-markdown'),
    import('remark-gfm'),
  ]);
  return {
    default: function MarkdownInner({ children }: { children: string }) {
      return <ReactMarkdown remarkPlugins={[remarkGfm]}>{children}</ReactMarkdown>;
    },
  };
});

export interface SystemRequirementsEditorProps {
  productId: string | null;
}

type Tab = 'edit' | 'preview';

const PLACEHOLDER = `# Supported OS
- Windows Server 2019 / 2022
- Oracle Linux 7.x / 8.x

# Database
- Oracle 19c
- PostgreSQL 15.x

# Hardware
- 8 vCPU, 16 GB RAM minimum for an application node.`;

/**
 * Markdown editor for the per-product "System Requirements" block. Whatever
 * lives here is auto-injected into the Release Notes (Internal) template
 * after the Cover/TOC and before the features list (see buildTree.ts).
 */
export const SystemRequirementsEditor: React.FC<SystemRequirementsEditorProps> = ({
  productId,
}) => {
  const branding = useProductBranding(productId);
  const update = useUpdateProductBranding();
  const stored = branding.data?.systemRequirementsMd ?? '';

  const [tab, setTab] = useState<Tab>('edit');
  const [local, setLocal] = useState<string>(stored);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => {
    setLocal(stored);
  }, [stored]);

  const commit = useCallback(() => {
    if (!productId) return;
    const next = local.trim() === '' ? null : local;
    if ((next ?? '') === (stored ?? '')) return;
    update.mutate(
      { productId, systemRequirementsMd: next },
      {
        onSuccess: () => setSavedAt(Date.now()),
      }
    );
  }, [productId, local, stored, update]);

  return (
    <div className="rb-sysreq" data-anchor="system-requirements">
      <div className="rb-sysreq__tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'edit'}
          className={`rb-sysreq__tab${tab === 'edit' ? ' rb-sysreq__tab--active' : ''}`}
          onClick={() => setTab('edit')}
        >
          Edit
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'preview'}
          className={`rb-sysreq__tab${tab === 'preview' ? ' rb-sysreq__tab--active' : ''}`}
          onClick={() => setTab('preview')}
        >
          Preview
        </button>
        <span className="rb-sysreq__status">
          {update.isPending
            ? 'Saving…'
            : savedAt
              ? `Saved ${new Date(savedAt).toLocaleTimeString()}`
              : ''}
        </span>
      </div>

      {tab === 'edit' ? (
        <textarea
          className="rb-sysreq__editor"
          value={local}
          onChange={(e) => setLocal(e.target.value)}
          onBlur={commit}
          placeholder={PLACEHOLDER}
          rows={12}
          disabled={!productId || update.isPending}
          aria-label="System requirements markdown"
        />
      ) : (
        <div className="rb-sysreq__preview rb-free-text">
          {local.trim() === '' ? (
            <p className="rb-sysreq__empty">Empty — no block will be rendered in reports.</p>
          ) : (
            <Suspense fallback={<pre className="rb-sysreq__fallback">{local}</pre>}>
              <Markdown>{local}</Markdown>
            </Suspense>
          )}
        </div>
      )}

      <p className="rb-sysreq__hint">
        Auto-injected into Release Notes (Internal) reports between the Cover page and the features
        list. Leave empty to skip the block. Editors can still override the content per-report via
        the Free Text block.
      </p>
    </div>
  );
};
